import { Button, IconImportBookshelf } from "@studyplus/boron-ui";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import ContentBox from "../../components/atoms/ContentBox";
import { BackgroundColor } from "../../components/atoms/SubLayout";
import { AI_QUESTION_BETA_AB_TEST_CODE } from "../../components/features/MultiSectionBatches/constants";
import { MultiSectionBatchesAdminSectionsSelectBox } from "../../components/features/MultiSectionBatches/MultiSectionBatchesAdminSectionsSelectBox";
import { MultiSectionBatchesDescription } from "../../components/features/MultiSectionBatches/MultiSectionBatchesDescription";
import { MultiSectionBatchesDownloadDateRangePicker } from "../../components/features/MultiSectionBatches/MultiSectionBatchesDownloadDateRangePicker";
import {
  adminSectionsSchema,
  useMultiSectionBatchesAdminSectionsSelectBox,
} from "../../components/features/MultiSectionBatches/useMultiSectionBatchesAdminSectionsSelectBox";
import { dateRangeSchema } from "../../components/features/MultiSectionBatches/useMultiSectionBatchesDateRangePicker";
import { BreadcrumbsView } from "../../components/general/BreadcrumbsView";
import { DateRange } from "../../components/general/DynamicDateRangePicker";
import enhanceHomePage from "../../hocs/enhanceHomePage";
import { useMultiSectionBatchesDownload } from "../../hooks/http/useMultiSectionBatchesDownload";
import { AdminSection } from "../../interfaces/AdminSection";
import OperatorInterface from "../../interfaces/OperatorInterface";

type Props = {
  currentOperator: OperatorInterface;
};

type Values = {
  dateRange: DateRange;
  adminSections: AdminSection[];
};

const Index = ({ currentOperator }: Props) => {
  const { adminSections, isLoading } =
    useMultiSectionBatchesAdminSectionsSelectBox({
      operatorId: currentOperator.id,
      abTestCode: AI_QUESTION_BETA_AB_TEST_CODE,
    });

  const { isPending, mutate } = useMultiSectionBatchesDownload({
    multiSectionBatchType: "download_ai_question_details",
  });

  const createDownloadFile = (values: Values) => {
    mutate({
      adminSections: values.adminSections,
      startDate: values.dateRange.startDate,
      endDate: values.dateRange.endDate,
    });
  };

  const canCreate = !isLoading && adminSections.length > 0 && !isPending;

  const breadcrumbItems = [
    { label: "校舎横断操作", url: "/multi_section_batches" },
    { label: "AI質問詳細のダウンロード (β)" },
  ];

  return (
    <div className="flex md:w-[90%] flex-col">
      <div className="py-5">
        <BreadcrumbsView items={breadcrumbItems} />
      </div>
      <ContentBox className="md:w-[80%] md:p-10">
        <div>
          <div className="flex items-center gap-6">
            <IconImportBookshelf className="h-[3rem] w-[3rem] text-gray-600" />
            <h2 className="text-xl">AI質問詳細のダウンロード (β)</h2>
          </div>
          <MultiSectionBatchesDescription
            isOnlyLearningManagementStudent={false}
            noteText="※連携中の生徒のみが出力されます。"
          />
          <Formik<Values>
            initialValues={{
              dateRange: { startDate: undefined, endDate: undefined },
              adminSections,
            }}
            enableReinitialize={true}
            onSubmit={(values) => {
              createDownloadFile(values);
            }}
            validationSchema={yup.object().shape({
              dateRange: dateRangeSchema,
              adminSections: adminSectionsSchema,
            })}
          >
            <Form>
              <div className="flex flex-col gap-8">
                <Field
                  name="adminSections"
                  component={MultiSectionBatchesAdminSectionsSelectBox}
                  isLoading={isLoading}
                />
                <Field
                  name="dateRange"
                  component={MultiSectionBatchesDownloadDateRangePicker}
                  allowedDateRangeDays={7}
                />
                <div className="flex justify-end">
                  <Button
                    className={classNames({
                      "hover:border-blue-300 hover:bg-blue-300": canCreate,
                    })}
                    disabled={!canCreate}
                    type="submit"
                  >
                    作成
                  </Button>
                </div>
              </div>
            </Form>
          </Formik>
        </div>
      </ContentBox>
    </div>
  );
};

export const MultiSectionBatchesDownloadAiQuestionDetailsPage = enhanceHomePage(
  Index,
  {
    title: "校舎横断操作",
    backgroundColor: BackgroundColor.Gray,
  },
);
